<template lang="html">
<div class="gw-hydrograph-chart">

    <div class="yearly-data-select">
        <v-layout row justify-space-between>
            <v-flex d-flex sm3>
                <v-select
                    width="200"
                    label="Select"
                    :items="yearlyDataAvailable"
                    v-model="yearlyDataDisplay"
                    multiple
                    max-height="400"
                    hint="Add yearly data"
                    persistent-hint
                ></v-select>
            </v-flex>
            <v-flex sm3 d-flex justify-center align-content-end>
                <v-btn @click="downloadChart" outline><v-icon>file_download</v-icon> Download PNG</v-btn>
            </v-flex>
        </v-layout>
    </div>
    <a style="display: none;" ref="downloadUrl" href="#"></a>

    <div class="chart-wrap">
        <v-btn class="zoom-btn" @click="resetZoom" icon>
            <v-icon>zoom_out</v-icon>
        </v-btn>
        <canvas :id="chartId"></canvas>
    </div>
</div>
</template>

<script>
    import yearlyChartData from '@/mixins/yearlyChart';
    import formatHistoricalMonthlyData from '@/mixins/formatHistoricalMonthlyData';
    import formatHistoricalMonthlyDateData from '@/mixins/formatHistoricalMonthlyDateData';
    import exportChart from '@/mixins/exportChart';
    import resetChart from '@/mixins/resetChart';
    import groundLevelData from '@/mixins/groundLevelData';
    import ie11ResizeMixin from '@/mixins/ie11ChartResize';
    import {
        container as chartContainerStructure,
        data as chartDataStructure,
    } from '@/chartData/gwLevelHydrograph';
    import Chart from 'chart.js';
    import moment from 'moment';

    export default {
        mixins: [
            yearlyChartData,
            formatHistoricalMonthlyData,
            formatHistoricalMonthlyDateData,
            exportChart,
            resetChart,
            groundLevelData,
            ie11ResizeMixin,
        ],
        data() {
            return {
                chart: null,
                chartId: 'canvas-gw-hydrograph',
                dataType: 'hydrograph',
            };
        },
        computed: {
            chartData() {
                return this.queryData.hydrograph;
            },
        },
        beforeDestroy() {
            this.chart.destroy();
        },
        mounted() {
            // Set chart interval to month
            this.chartInterval = 'm';
            if (this.chartData) {
                this.updateChart();
            }
        },
        methods: {
            /**
             * create datasets and chart context object
             * @return {Object} chart.js context object
             */
            formatDatasets() {
                const historicalData = this.formatHistoricalData(this.chartData.historical, (date, e) => {
                    const formatted = {
                        d: date,
                        a: e.a,
                        min: e.min,
                        max: e.max,
                        p25: e.p25,
                        p75: e.p75,
                    };
                    return formatted;
                });
                const currentData = this.formatCurrentData(this.chartData.current);
                const currentDates = currentData.map(d => d.d);

                const chartData = chartDataStructure(currentDates, currentData, historicalData);

                const yearlyOverlayDatasets = this.formatYearlyData(this.formatHistoricalDateData);
                chartData.datasets.unshift(...yearlyOverlayDatasets);
                return chartData;
            },
            /**
             * set up the chart and do the initial render
             */
            renderChart() {
                const chartData = this.formatDatasets();

                this.chart = new Chart(this.chartId, chartContainerStructure(chartData));
            },
            /**
             * update the datasets and re-render the chart
             */
            updateChart() {
                // do the initial render if the chart isn't defined yet
                if (this.chart === null) {
                    this.renderChart();
                    return;
                }
                this.chart.data = this.formatDatasets();
                this.chart.update({
                    duration: 0,
                });
            },
            /**
             * format current hydrograph data with date objects
             * @param  {Array} currentData hydrograph data with string dates
             * @return {Array}             hydrograph data with moment.js dates
             */
            formatCurrentData(currentData) {
                const dataWithDates = currentData.map((d) => {
                    const date = moment(d.d);
                    return {
                        d: date,
                        v: d.v,
                    };
                });

                return dataWithDates;
            },
        },
        watch: {
            /**
             * update the chart when the data changes
             */
            chartData(val) {
                this.yearlyData = {};
                this.yearlyDataDisplay = [];
                if (val) {
                    this.updateChart();
                }
            },
            updateData(val) {
                if (val) {
                    this.updateChart();
                    this.updateData = false;
                }
            },
        },
    };
</script>

<style lang="scss">
    .gw-hydrograph-chart {
        $tabbarHeight: 128px;
        $toolbarHeight: 5rem;
        $yPadding: 5rem;

        .yearly-data-select {
            height: $toolbarHeight;
        }
        .chart-wrap {
            // 100vh minus the toolbars and 5rem padding
            height: calc(100vh - #{$tabbarHeight} - #{$toolbarHeight} - #{$yPadding});
            min-height: 20rem;
        }
    }
    .zoom-btn {
        position: absolute;
        top: 115px;
        right: 40px;
    }
</style>



// WEBPACK FOOTER //
// src/components/charts/GwLevelHydrograph.vue